import { useState } from 'react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { submissionsAPI } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { 
  DocumentTextIcon, 
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FunnelIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { format } from 'date-fns'
import clsx from 'clsx'

const statusIcons = {
  submitted: ClockIcon,
  under_review: ExclamationTriangleIcon,
  evaluated: CheckCircleIcon,
}

const statusColors = {
  submitted: 'text-yellow-600 bg-yellow-100',
  under_review: 'text-blue-600 bg-blue-100',
  evaluated: 'text-green-600 bg-green-100',
}

const statusLabels = {
  submitted: 'Submitted',
  under_review: 'Under Review',
  evaluated: 'Evaluated',
}

export default function SubmissionsPage() {
  const { user } = useAuth()
  const [filters, setFilters] = useState({
    status: '',
    challenge: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  })

  const { data: submissionsData, isLoading, error, refetch } = useQuery(
    ['my-submissions', filters],
    () => submissionsAPI.getMySubmissions({
      ...filters,
      limit: 50
    }),
    {
      select: (response) => response.data
    }
  )

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const handleDelete = async (submissionId) => {
    if (window.confirm('Are you sure you want to delete this submission?')) {
      try {
        await submissionsAPI.delete(submissionId)
        refetch()
      } catch (error) {
        console.error('Failed to delete submission:', error)
      }
    }
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error loading submissions</h2>
          <p className="text-gray-600">Please try again later.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                My Submissions
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Track and manage your challenge submissions
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4">
              <Link
                to="/challenges"
                className="btn-primary"
              >
                Submit New Solution
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">All Status</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="evaluated">Evaluated</option>
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort By
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="created_at">Date Submitted</option>
                <option value="score">Score</option>
                <option value="title">Title</option>
                <option value="status">Status</option>
              </select>
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order
              </label>
              <select
                value={filters.sortOrder}
                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={() => setFilters({
                  status: '',
                  challenge: '',
                  sortBy: 'created_at',
                  sortOrder: 'desc'
                })}
                className="btn-outline w-full"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Submissions List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-gray-700">
                Showing {submissionsData?.submissions?.length || 0} submissions
              </p>
            </div>

            {submissionsData?.submissions?.length > 0 ? (
              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                  {submissionsData.submissions.map((submission) => {
                    const StatusIcon = statusIcons[submission.status]
                    return (
                      <li key={submission.id}>
                        <div className="px-4 py-4 sm:px-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center min-w-0 flex-1">
                              <div className={`flex-shrink-0 p-2 rounded-full ${statusColors[submission.status]}`}>
                                <StatusIcon className="h-5 w-5" />
                              </div>
                              <div className="ml-4 min-w-0 flex-1">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-medium text-primary-600 truncate">
                                      {submission.title}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      {submission.challenge_title}
                                    </p>
                                  </div>
                                  <div className="ml-2 flex-shrink-0 flex">
                                    <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      submission.status === 'evaluated' ? 'bg-green-100 text-green-800' :
                                      submission.status === 'under_review' ? 'bg-blue-100 text-blue-800' :
                                      'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {statusLabels[submission.status]}
                                    </p>
                                  </div>
                                </div>
                                <div className="mt-2 sm:flex sm:justify-between">
                                  <div className="sm:flex">
                                    <p className="flex items-center text-sm text-gray-500">
                                      <DocumentTextIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                      Submitted {format(new Date(submission.created_at), 'MMM dd, yyyy')}
                                    </p>
                                    {submission.score && (
                                      <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                                        <CheckCircleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                        Score: {submission.score}
                                      </p>
                                    )}
                                  </div>
                                  <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                                    <div className="flex space-x-2">
                                      <Link
                                        to={`/submissions/${submission.id}`}
                                        className="text-primary-600 hover:text-primary-500"
                                      >
                                        <EyeIcon className="h-4 w-4" />
                                      </Link>
                                      {submission.status === 'submitted' && (
                                        <>
                                          <Link
                                            to={`/submissions/${submission.id}/edit`}
                                            className="text-gray-600 hover:text-gray-500"
                                          >
                                            <PencilIcon className="h-4 w-4" />
                                          </Link>
                                          <button
                                            onClick={() => handleDelete(submission.id)}
                                            className="text-red-600 hover:text-red-500"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          {submission.description && (
                            <div className="mt-2 ml-16">
                              <p className="text-sm text-gray-600 line-clamp-2">
                                {submission.description}
                              </p>
                            </div>
                          )}
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No submissions found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {filters.status || filters.challenge 
                    ? 'Try adjusting your filters or create your first submission.'
                    : 'Get started by participating in a challenge and submitting your solution.'
                  }
                </p>
                <div className="mt-6">
                  <Link
                    to="/challenges"
                    className="btn-primary"
                  >
                    Browse Challenges
                  </Link>
                </div>
              </div>
            )}
          </>
        )}

        {/* Statistics */}
        {submissionsData?.statistics && (
          <div className="mt-8 bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Submission Statistics</h3>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <div className="bg-gray-50 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <DocumentTextIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Submissions
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {submissionsData.statistics.total}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CheckCircleIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Evaluated
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {submissionsData.statistics.evaluated}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Under Review
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {submissionsData.statistics.under_review}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ClockIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Average Score
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {submissionsData.statistics.averageScore || 'N/A'}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}